import moment from 'moment';
import { useRouter } from 'vue-router';
import { useSessionsStore } from '@/stores/sessions';
import { useCoursesStore } from '@/stores/courses';
import { useOrganizationStore } from '@/stores/organization';
import { PERFORM_URL } from '@/common/config';
import i18n from '@/i18n';
import md5 from 'md5';
import {
  colorShade,
  hexToRgb,
  hexToRgbNumbers,
  isLight,
} from '@/common/color.helper';

// Configure moment locale
moment.locale(i18n.global.locale.value);

export function useHelper() {
  const router = useRouter();
  const sessionsStore = useSessionsStore();
  const coursesStore = useCoursesStore();
  const organizationStore = useOrganizationStore();
  const getOrganization = () => organizationStore.organization;

  // NOTE: this.$Progress is not available in composables.
  // It needs to be handled differently in Vue 3, maybe via a plugin or provide/inject.
  // For now, related lines are commented out.
  // const progress = inject('progress');

  const setOrgCSS = (reset = false) => {
    let actionColor;
    let primaryTextColor;
    let primaryBackgroundColor;
    let additiveBgColor;

    const organization = getOrganization();
    const colors = organization?.colors ?? {};

    if (reset || !organization) {
      actionColor = '#00B3A4';
      primaryTextColor = '#ffffff';
      primaryBackgroundColor = '#000000';
    } else if (organization) {
      actionColor = colors.actionColor ?? '#00B3A4';
      primaryTextColor = colors.primaryTextColor ?? '#ffffff';
      primaryBackgroundColor = colors.primaryBackgroundColor ?? '#000000';
      additiveBgColor = colors.additiveBackgroundColor ?? primaryBackgroundColor;
    }

    document.documentElement.style.setProperty('--primary', hexToRgb(actionColor));
    // if (progress) progress.setColor(hexToRgb(actionColor));
    document.documentElement.style.setProperty('--primary-numbers', hexToRgbNumbers(actionColor));
    document.documentElement.style.setProperty('--bs-primary', hexToRgb(actionColor));
    document.documentElement.style.setProperty('--bs-primary-rgb', hexToRgbNumbers(actionColor));
    document.documentElement.style.setProperty('--primary-darker', hexToRgb(colorShade(actionColor, -20)));
    document.documentElement.style.setProperty('--primary-lighter', hexToRgb(colorShade(actionColor, +20)));
    document.documentElement.style.setProperty('--secondary', hexToRgb(primaryTextColor));
    document.documentElement.style.setProperty('--secondary-numbers', hexToRgbNumbers(primaryTextColor));
    document.documentElement.style.setProperty('--bs-secondary', hexToRgb(primaryTextColor));
    document.documentElement.style.setProperty('--bs-secondary-rgb', hexToRgbNumbers(primaryTextColor));
    document.documentElement.style.setProperty('--bs-body-color', hexToRgb(primaryTextColor));
    document.documentElement.style.setProperty('--bs-body-color-rgb', hexToRgbNumbers(primaryTextColor));
    document.documentElement.style.setProperty('--bs-emphasis-color', hexToRgb(primaryTextColor));
    document.documentElement.style.setProperty('--bs-emphasis-color-rgb', hexToRgbNumbers(primaryTextColor));
    document.documentElement.style.setProperty('--bs-link-color', hexToRgb(primaryTextColor));
    document.documentElement.style.setProperty('--bs-link-color-rgb', hexToRgbNumbers(primaryTextColor));
    document.documentElement.style.setProperty('--secondary-lighter', hexToRgb(colorShade(primaryTextColor, +20)));
    document.documentElement.style.setProperty('--secondary-darker', hexToRgb(colorShade(primaryTextColor, -40)));
    document.documentElement.style.setProperty('--bs-link-hover-color', hexToRgb(colorShade(primaryTextColor, -40)));
    document.documentElement.style.setProperty('--background', hexToRgb(primaryBackgroundColor));
    document.documentElement.style.setProperty('--background-numbers', hexToRgbNumbers(primaryBackgroundColor));
    document.documentElement.style.setProperty('--bs-body-bg', hexToRgb(primaryBackgroundColor));

    if (isLight(primaryBackgroundColor)) {
      document.documentElement.style.setProperty('--background-transparent', `${colorShade(primaryBackgroundColor, -20)}CC`);
    } else {
      document.documentElement.style.setProperty('--background-transparent', `${colorShade(primaryBackgroundColor, +20)}CC`);
    }

    document.documentElement.style.setProperty('--secondary-background', hexToRgb(additiveBgColor));
  };

  const handleSessionClick = (sessionId) => {
    animateCSS('.card-last-session', 'fadeOutUp', 'slower');
    sessionsStore.setCurrentSession(null);
    coursesStore.setCurrentCourse(null);
    router.push({ name: 'SessionView', params: { sessionId } });
  };

  const removeClassStartsWith = (node, className) => {
    [...node.classList].forEach((v) => {
      if (v.startsWith(className)) {
        node.classList.remove(v);
      }
    });
  };

  const animateCSS = (element, animation, speed = null, delay = null, prefix = 'animate__') => {
    return new Promise((resolve) => {
      const animationName = `${prefix}${animation}`;
      const speedName = `${prefix}${speed}`;
      const delayName = `${prefix}delay-${delay}`;
      const node = document.querySelector(element);
      if (!node) return;
      removeClassStartsWith(node, prefix);

      node.classList.add(`${prefix}animated`, animationName);

      if (speed) {
        node.classList.add(speedName);
      }

      if (delay) {
        node.classList.add(delayName);
      }

      function handleAnimationEnd(event) {
        event.stopPropagation();
        node.classList.remove(`${prefix}animated`, animationName);
        resolve('Animation ended');
      }

      node.addEventListener('animationend', handleAnimationEnd, { once: true });
    });
  };

  const getObjectThumbnail = (objectType, organizationId, objectId, thumbnail) => {
    return `${PERFORM_URL}/object/objectThumbnail/${objectType}/${organizationId}/${objectId}/${thumbnail}`;
  };

  const getBadgeThumbnail = (badgeId) => {
    return `${PERFORM_URL}/badge/badgeThumbnail/${badgeId}`;
  };

  const getOrganizationWallpaper = (organizationId, vertical = false) => {
    const direction = vertical ? 'vertical' : 'horizontal';
    return `${PERFORM_URL}/img/organization/${organizationId}/background/${direction}`;
  };

  const getOrganizationLogo = (organizationId) => {
    return `${PERFORM_URL}/img/organization/${organizationId}/background/logo`;
  };

  const isMobileOrTablet = () => window.screen.width <= 1200;
  const isMobile = () => window.screen.width <= 760;

  const formatDate = (date) => moment(date, 'YYYY-MM-DD h:mm:ss').format('DD/MM/YYYY');

  const formatDateFull = (date) => {
    moment.locale(i18n.global.locale.value);
    return moment.utc(date, 'YYYY-MM-DD h:mm:ss').local().format('LLLL');
  };

  const padLeft = (_n, width) => {
    const z = '0';
    const n = `${_n}`;
    return n.length >= width ? n : new Array(width - n.length + 1).join(z) + n;
  };

  const formatSeconds = (value) => {
    const hours = Math.floor(value / 3600);
    const minutes = Math.floor((value / 60) % 60);
    const seconds = value % 60;
    return `${padLeft(hours, 2)}:${padLeft(minutes, 2)}:${padLeft(seconds, 2)}`;
  };

  const formatPrivacyLevel = (name, privacyLevel) => {
    const names = name.split('|');
    const firstName = names[0];
    const lastName = names[1];
    switch (privacyLevel) {
      case 1: return `${firstName} ${lastName[0]}.`;
      case 2: return `${firstName[0]}. ${lastName[0]}.`;
      case 3: return `${firstName[0]}${lastName[0]}`;
      default: return `${firstName} ${lastName}`;
    }
  };

  const isInferiorToToday = (date) => {
    if (!date) {
      return false;
    }
    const sessionDate = new Date(date);
    const now = new Date();
    return sessionDate <= now;
  };

  const getGravatarURL = (email) => `https://s.gravatar.com/avatar/${md5(email)}?s=150&d=404`;

  const validateEmail = (email) => {
    return email.match(/^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}))$/);
  };

  const checkGravatar = (email) => {
    if (validateEmail(email)) {
      const xhr = new XMLHttpRequest();
      xhr.open('HEAD', getGravatarURL(email), false);
      xhr.send();
      return xhr.status === 200;
    }
    return false;
  };

  const validatePassword = (password) => {
    const regex = /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^/&*-]).{7,}$/;
    return regex.test(password);
  };

  const blockedText = (condition) => i18n.global.t('resource.label.access.condition') + condition;
  
  // Exposing methods from the composable
  return {
    setOrgCSS,
    handleSessionClick,
    animateCSS,
    getObjectThumbnail,
    getBadgeThumbnail,
    getOrganizationWallpaper,
    getOrganizationLogo,
    isMobileOrTablet,
    isMobile,
    formatDate,
    formatDateFull,
    padLeft,
    formatSeconds,
    formatPrivacyLevel,
    isInferiorToToday,
    getGravatarURL,
    validateEmail,
    checkGravatar,
    validatePassword,
    blockedText,
    // Note: Mixin's data properties (sessionFilter, sessionQuery) and methods 
    // (filterSession, handleSessionSearch, getSessionParams) were related to session filtering
    // and directly manipulated the Vuex store. This logic should be moved into the `sessions` Pinia store.
  };
}
